
import React from "react";
import { useSearchParams } from "@rivtn/noseur";
import { i18nManager } from "../i18n";
import BlogCard from "./common/BlogCard";
import PageHead from "./common/PageHead";
import Pagination from "./common/Pagination";
import FilterTags from "./common/FilterTags";
import { Utils } from "../../../../utils";
import styles from "../assets/style.module.css";
import { ApiResponseData, BaseService, IHostBlogArticle, IHostBlogCategory, IHostWebsite, OmneHostService, SanitizedResponse } from "../../../../services";

function BlogDetail({ article, category, onBack }: { article: IHostBlogArticle; category?: string; onBack: () => void; }) {

    const labels = i18nManager.Labels.blog;

    React.useEffect(() => {
        window.scrollTo({ top: 0 });
    }, [article.external_id]);

    return (<section className={styles["section-page"]}>
        <div className={styles["container-narrow"]}>
            <button className={styles["back-btn"]} onClick={onBack}><i className="fa fa-arrow-left" /> {labels.back}</button>
            <span className={styles["tag"]}>[ {category ? `${category} · ` : ""}{Utils.fineDate(new Date(article.updated_at))} ]</span>
            <h1 className={styles["h-03"]} style={{ margin: "14px 0 32px" }}>{article.title}</h1>
            {article.extra_data?.featured_image && (<img className={styles["article-img"]} src={article.extra_data.featured_image} alt={article.title} />)}
            <div className={styles["article-body"]} dangerouslySetInnerHTML={{ __html: Utils.markdownToHtml(article.content) }} />
        </div>
    </section>);
}

export interface BlogProps {
    website: IHostWebsite;
}

function Blog({ website }: BlogProps) {

    const labels = i18nManager.Labels.blog;
    const omnehostService = OmneHostService.getInstance();
    const [selected, setSelected] = React.useState<IHostBlogArticle>();
    const [categories, setCategories] = React.useState<IHostBlogCategory[]>([]);
    const [articles, setArticles] = React.useState<ApiResponseData<IHostBlogArticle>>();
    const { page, setPage, category, setCategory, article, setArticle } = useSearchParams({ defaultInit: window.location.search, params: { page: 1, category: "", article: "" } });
    const categoryName = (id?: string) => categories.find((c) => c.external_id === id)?.name;

    React.useEffect(() => {
        omnehostService.queryHostBlogCategories(website.host.external_id, {}).then(({ content }: SanitizedResponse<IHostBlogCategory>) => {
            setCategories(content ?? []);
        }).catch(BaseService.reportError);
    }, [omnehostService, website.host.external_id]);
    React.useEffect(fetchInformation, [omnehostService, page, category]);
    React.useEffect(() => {
        if (!article) setSelected(undefined);
    }, [article]);

    if (selected) {
        return (<BlogDetail article={selected} category={categoryName(selected.host_category_id)} onBack={() => {
            setSelected(undefined);
            setArticle(undefined);
        }} />);
    }

    return (<>
        <PageHead tag={labels.kicker} title={labels.title} subtitle={labels.desc} />
        <section className={styles["section-page"]}>
            <div className={styles["container"]}>
                {categories.length > 0 && (<FilterTags value={`${category}`} onChange={setCategory} options={[["", labels.all], ...categories.map((c) => [c.external_id, c.name] as [string, string])]} />)}
                {(!!articles?.content)
                    ? (articles.content.length > 0
                        ? (<div className={styles["blog-grid"]}>
                            {articles.content.map((a) => (<BlogCard key={a.external_id} article={a} category={categoryName(a.host_category_id)} onSelect={(a) => {
                                setSelected(a);
                                setArticle(a.external_id);
                            }} />))}
                        </div>)
                        : Utils.emptyState())
                    : Utils.loadingState()}
                <Pagination page={parseInt(`${page}`)} rowsPerPage={articles?.size} totalRecords={articles?.total_elements} onPageChange={setPage} />
            </div>
        </section>
    </>);

    function fetchInformation() {
        setArticles({});
        omnehostService.queryHostBlogArticles(website.host.external_id, {
            page,
            sort: "updated_at,DESC",
            host_category_id: category,
        }).then(({ apiResponse, content }: SanitizedResponse<IHostBlogArticle>) => {
            setArticles(apiResponse.data!);
            const match = (content ?? []).find((a) => a.external_id === article);
            if (match) setSelected(match);
        }).catch(BaseService.reportError);
    }
}

export default Blog;
